import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from '../queries/querykeys'
import { BusinessAPI } from '../api/business.api'
import { UserAuth } from '../context/AuthContext'
import { LibroDiario } from '../models/LibroContable'

interface LibroMayorFilters {
  searchQuery?: string
  comprobanteFilter?: string
  clienteFilter?: string
}

interface UseInfiniteLibroMayorParams {
  businessId: number
  idCuenta: number
  dateFrom: { year: number, month: number }
  dateTo: { year: number, month: number }
  limit: number
  filters: LibroMayorFilters
}

export const useInfiniteLibroMayor = ({
  businessId,
  idCuenta,
  dateFrom,
  dateTo,
  limit = 30,
  filters
}: UseInfiniteLibroMayorParams) => {
  const { isAuthenticated } = UserAuth()
  const businessAPI = new BusinessAPI()

  return useInfiniteQuery({
    enabled: isAuthenticated && !!businessId,
    queryKey: [
      QueryKeys.LIBRO_MAYOR,
      businessId,
      idCuenta,
      dateFrom,
      dateTo,
      limit,
      filters
    ],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await businessAPI.getDataLibroMayor({
        businessId,
        idCuenta,
        dateFrom,
        dateTo,
        limit,
        page: pageParam
      })

      const libroMayorData = response.data

      const isPaginatedResponse = 'count' in libroMayorData && 'libros' in libroMayorData
      
      const libros = isPaginatedResponse
        ? (libroMayorData as { libros: LibroDiario[], count: number }).libros
        : libroMayorData as LibroDiario[]
      
      const count = isPaginatedResponse
        ? (libroMayorData as { count: number }).count
        : libros.length

      if (!libros || libros.length === 0) {
        return {
          data: [],
          totalPages: 0,
          currentPage: pageParam,
          hasNextPage: false,
          count: 0
        }
      }

      const totalPages = Math.ceil(count / limit)
      
      return {
        data: libros,
        totalPages,
        currentPage: pageParam,
        hasNextPage: pageParam + 1 < totalPages,
        count
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined
    },
    initialPageParam: 0
  })
}